import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppSelector } from 'app/store';
import { selectFeatureFlags } from 'app/store/featureFlagsSlice';
import { highnoteService } from 'app/services/highnote.service';
import type { HnAddress, HnError, HnValidateAddressInput, HnAddressValidationResponse } from 'CDK_API';

// Schema declares `labels: [String]`; this union is the set HN actually returns today.
export type HnAddressLabel =
  | 'PO_BOX'
  | 'BUSINESS'
  | 'RESIDENTIAL'
  | 'CMRA'
  | 'VACANT'
  | 'INACTIVE'
  | 'UNSECURED_LOCATION'
  | 'UNCONFIRMED_EXTENDED_ADDRESS';

export type ValidationStatus =
  | { kind: 'idle' }
  | { kind: 'pending' }
  | { kind: 'validated'; labels: HnAddressLabel[]; validatedValues: HnValidateAddressInput }
  | {
      kind: 'with_changes';
      labels: HnAddressLabel[];
      corrected: HnValidateAddressInput;
      original: HnValidateAddressInput;
      /** Field names HN flagged as changed (e.g., ["POSTAL_CODE"]). Optional UX hint. */
      componentsChanged: string[];
    }
  | { kind: 'incomplete'; errors: HnError[] }
  | { kind: 'invalid'; errors: HnError[] }
  | { kind: 'transport_error'; message?: string };

export type UseAddressValidationOptions = {
  debounceMs?: number;
  /** Override the feature-flag-derived enabled state. Defaults to flag value. */
  enabled?: boolean;
};

export type UseAddressValidationReturn = {
  status: ValidationStatus;
  validate: () => Promise<ValidationStatus>;
  validateOnSubmit: () => Promise<ValidationStatus>;
  resolveCorrections: (decision: 'accept' | 'decline') => void;
};

const REQUIRED_FIELDS: (keyof HnValidateAddressInput)[] = [
  'streetAddress',
  'postalCode',
  'locality',
  'region',
  'countryCodeAlpha3',
];

export function isAddressComplete(
  address: Partial<HnValidateAddressInput> | null | undefined
): address is HnValidateAddressInput {
  if (!address) return false;
  return REQUIRED_FIELDS.every((field) => {
    const value = address[field];
    return typeof value === 'string' && value.trim().length > 0;
  });
}

function addressesMatch(a: HnValidateAddressInput, b: Partial<HnValidateAddressInput>): boolean {
  return (
    a.streetAddress === b.streetAddress &&
    (a.extendedAddress ?? '') === (b.extendedAddress ?? '') &&
    a.postalCode === b.postalCode &&
    a.locality === b.locality &&
    a.region === b.region &&
    a.countryCodeAlpha3 === b.countryCodeAlpha3
  );
}

function mergeCorrected(original: HnValidateAddressInput, corrected: HnAddress): HnValidateAddressInput {
  return {
    streetAddress: corrected.streetAddress ?? original.streetAddress,
    extendedAddress: corrected.extendedAddress ?? original.extendedAddress,
    postalCode: corrected.postalCode ?? original.postalCode,
    locality: corrected.locality ?? original.locality,
    region: corrected.region ?? original.region,
    countryCodeAlpha3: corrected.countryCodeAlpha3 ?? original.countryCodeAlpha3,
  };
}

function mapResponseToStatus(
  response: HnAddressValidationResponse,
  current: HnValidateAddressInput
): ValidationStatus {
  const labels = (response.labels?.filter((l): l is string => !!l) ?? []) as HnAddressLabel[];
  const errors = (response.errors?.filter((e): e is HnError => !!e) ?? []) as HnError[];

  switch (response.status) {
    case 'VALIDATED':
      return { kind: 'validated', labels, validatedValues: current };
    case 'VALIDATED_WITH_CHANGES': {
      const corrected = response.correctedAddress
        ? mergeCorrected(current, response.correctedAddress)
        : current;
      const componentsChanged =
        response.componentsChanged?.filter((c: string | null): c is string => !!c) ?? [];
      return { kind: 'with_changes', labels, corrected, original: current, componentsChanged };
    }
    case 'INCOMPLETE':
      return { kind: 'incomplete', errors };
    case 'INVALID':
      return { kind: 'invalid', errors };
    case 'TRANSPORT_ERROR':
      return { kind: 'transport_error', message: response.message ?? undefined };
    default:
      return { kind: 'invalid', errors };
  }
}

export function useAddressValidation(
  address: Partial<HnValidateAddressInput> | null | undefined,
  options: UseAddressValidationOptions = {}
): UseAddressValidationReturn {
  const debounceMs = options.debounceMs ?? 300;
  const featureFlags = useAppSelector(selectFeatureFlags);
  const flagEnabled = featureFlags.flags?.['enableAddressValidation']?.enabled === true;
  const enabled = options.enabled ?? flagEnabled;

  const [status, setStatus] = useState<ValidationStatus>({ kind: 'idle' });

  const statusRef = useRef<ValidationStatus>(status);
  const enabledRef = useRef<boolean>(enabled);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const validatedValuesRef = useRef<HnValidateAddressInput | null>(null);
  const addressRef = useRef<Partial<HnValidateAddressInput> | null | undefined>(address);
  // Bumped at the start of every in-flight fetch; lets us discard stale responses
  // when the user edits the address before an earlier fetch resolves.
  const generationRef = useRef(0);

  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  useEffect(() => {
    addressRef.current = address;
    if (validatedValuesRef.current && address && !addressesMatch(validatedValuesRef.current, address)) {
      validatedValuesRef.current = null;
      setStatus({ kind: 'idle' });
    }
  }, [address]);

  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, []);

  const performValidation = useCallback(async (): Promise<ValidationStatus> => {
    const current = addressRef.current;
    if (!enabledRef.current) return { kind: 'idle' };
    if (!isAddressComplete(current)) return { kind: 'idle' };

    if (validatedValuesRef.current && addressesMatch(validatedValuesRef.current, current)) {
      return statusRef.current;
    }

    const generation = ++generationRef.current;
    setStatus({ kind: 'pending' });

    try {
      const response = await highnoteService.hnValidateAddress(current);
      // Discard the response if a newer fetch was started in the meantime —
      // the older payload describes an address the user has since edited.
      if (generation !== generationRef.current) return statusRef.current;
      if (!response) {
        const next: ValidationStatus = { kind: 'transport_error' };
        setStatus(next);
        return next;
      }
      const next = mapResponseToStatus(response, current);
      if (next.kind === 'validated') {
        validatedValuesRef.current = next.validatedValues;
      } else if (next.kind === 'with_changes') {
        validatedValuesRef.current = next.original;
      } else {
        validatedValuesRef.current = null;
      }
      setStatus(next);
      return next;
    } catch (err) {
      if (generation !== generationRef.current) return statusRef.current;
      const message = err instanceof Error ? err.message : undefined;
      const next: ValidationStatus = { kind: 'transport_error', message };
      setStatus(next);
      return next;
    }
  }, []);

  const validate = useCallback((): Promise<ValidationStatus> => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
      debounceTimer.current = null;
    }
    return new Promise((resolve) => {
      debounceTimer.current = setTimeout(async () => {
        debounceTimer.current = null;
        const result = await performValidation();
        resolve(result);
      }, debounceMs);
    });
  }, [debounceMs, performValidation]);

  const validateOnSubmit = useCallback(async (): Promise<ValidationStatus> => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
      debounceTimer.current = null;
    }
    return performValidation();
  }, [performValidation]);

  const resolveCorrections = useCallback((decision: 'accept' | 'decline') => {
    const current = statusRef.current;
    if (current.kind !== 'with_changes') return;
    const cached = decision === 'accept' ? current.corrected : current.original;
    validatedValuesRef.current = cached;
    setStatus({
      kind: 'validated',
      labels: current.labels,
      validatedValues: cached,
    });
  }, []);

  return { status, validate, validateOnSubmit, resolveCorrections };
}
